import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import languages from "@/data/languages.json";
import { hasSameBaseLanguage } from "@/lib/learning-mode";
import { getTranslationLanguageName } from "@/lib/translation-language";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";

interface TranslationLanguageSelectProps {
  targetLanguageCode: string;
  languageCode: string;
  onLanguageChange: (code: string) => void;
}

export function TranslationLanguageSelect({
  targetLanguageCode,
  languageCode,
  onLanguageChange,
}: TranslationLanguageSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const options = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(languages).filter(
          ([, code]) => !hasSameBaseLanguage(targetLanguageCode, code)
        )
      ),
    [targetLanguageCode]
  );

  return (
    <div className="px-3 py-2 text-sm font-medium text-white/85">
      <span className="mb-2 block">Translation language</span>
      <ResponsiveLanguagePicker
        open={open}
        onOpenChange={setOpen}
        trigger={
          <button
            type="button"
            aria-label={`Translation language: ${getTranslationLanguageName(languageCode)}`}
            aria-expanded={open}
            className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-[#FD954F]/35 bg-white/[0.06] px-3 py-2 text-left text-sm font-medium text-white outline-none transition-colors hover:border-[#FD954F]/60 focus-visible:ring-2 focus-visible:ring-[#FD954F]/30"
          >
            <span>{getTranslationLanguageName(languageCode)}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-white/55" aria-hidden="true" />
          </button>
        }
        languages={options}
        selectedLanguageCode={languageCode}
        onSelectLanguage={onLanguageChange}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        title="Choose a translation language"
        desktopAlign="end"
      />
      <span className="mt-1 block text-xs font-normal text-white/45">
        For translations and explanations.
      </span>
    </div>
  );
}
